import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api, { formatMartiniqueDate } from "@/lib/api";
import { Sailboat, Trophy, Flame, ArrowRight, Waves, Anchor } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL = {
  upcoming: { text: "Ouvert", cls: "bg-[color:var(--palm)] text-[color:var(--deep)]" },
  closed: { text: "Fermé", cls: "bg-amber-100 text-amber-700" },
  settled: { text: "Réglé", cls: "bg-slate-200 text-slate-600" },
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [yoles, setYoles] = useState([]);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      navigate("/admin", { replace: true });
      return;
    }
    (async () => {
      try {
        const [s, l, y, se] = await Promise.all([
          api.get("/stages"),
          api.get("/leaderboard"),
          api.get("/yoles"),
          api.get("/seasons/current"),
        ]);
        setStages(s.data);
        setLeaderboard(l.data.slice(0, 5));
        setYoles(y.data);
        setSeason(se.data);
      } catch (e) {
        // ignore
      }
    })();
  }, [user, navigate]);

  const upcoming = stages.filter((s) => s.status === "upcoming");
  const yoleById = Object.fromEntries(yoles.map((y) => [y.id, y]));
  const myRank = leaderboard.findIndex((l) => l.user_id === user?.id) + 1;

  return (
    <div className="space-y-8">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl bg-[color:var(--deep)] text-white p-6 md:p-10"
        data-testid="home-hero"
      >
        <img
          src="/branding/logo_full.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-right opacity-40 md:opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--deep)] via-[color:var(--deep)]/85 to-[color:var(--deep)]/10" />
        <div className="relative">
          <div className="brand-signature text-[11px] mb-3">
            {season?.name || "Prono. Vibré. Gagné."}
          </div>
          <h1 className="brand-wordmark text-white text-4xl sm:text-5xl lg:text-6xl">
            Salut {user?.name?.split(" ")[0] || "matelot"},<br />
            <span className="text-[color:var(--sunshine)]">à toi de miser.</span>
          </h1>
          <div className="mt-6 flex flex-wrap gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Ton solde</div>
              <div className="font-mono text-3xl font-bold tabular-nums">
                {user?.wallet_points ?? 0}<span className="text-xs opacity-60 ml-1">pts</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Classement</div>
              <div className="font-mono text-3xl font-bold tabular-nums">
                {myRank > 0 ? `#${myRank}` : "—"}
              </div>
            </div>
            <Link
              to="/etapes"
              data-testid="hero-cta"
              className="ml-auto inline-flex items-center gap-2 px-5 py-3 rounded-full btn-cta text-sm transition-all hover:scale-105"
            >
              Voir les étapes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* Bento */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile label="Étapes ouvertes" value={upcoming.length} icon={Sailboat} tint="coral" testid="stat-upcoming" />
        <StatTile label="Yoles engagées" value={yoles.length} icon={Anchor} tint="ocean" testid="stat-yoles" />
        <StatTile label="Parieurs actifs" value={leaderboard.length} icon={Flame} tint="sunshine" testid="stat-users" />
      </section>

      {/* Upcoming stages */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold text-[color:var(--deep)]">Prochaines étapes</h2>
          <Link to="/etapes" className="text-xs font-bold text-[color:var(--coral)] hover:underline" data-testid="see-all-stages">
            Tout voir →
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <EmptyStages />
        ) : (
          <div className="space-y-3">
            {upcoming.slice(0, 3).map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/etapes/${s.id}`}
                  data-testid={`stage-card-${s.id}`}
                  className="bento-tile block bg-white border border-slate-200 rounded-2xl p-5 hover:border-[color:var(--coral)]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Étape {s.stage_number} · {formatMartiniqueDate(s.date, { month: "short" })}
                      </div>
                      <div className="mt-1 font-display font-extrabold text-lg text-[color:var(--deep)] truncate">
                        {s.name}
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 truncate">
                        {s.start_village} → {s.end_village}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={`${STATUS_LABEL[s.status].cls} border-0 font-bold text-[10px] px-2.5 py-1 rounded-full`}>
                        {STATUS_LABEL[s.status].text}
                      </Badge>
                      <div className="font-mono text-xs text-slate-500">
                        x{s.stage_winner_odds}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Mini leaderboard */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold text-[color:var(--deep)]">Top parieurs</h2>
          <Link to="/classement" className="text-xs font-bold text-[color:var(--coral)] hover:underline" data-testid="see-leaderboard">
            Classement complet →
          </Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {leaderboard.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">Aucun parieur pour l'instant.</div>
          )}
          {leaderboard.map((row, i) => (
            <div key={row.user_id} className="flex items-center gap-4 px-5 py-3.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black font-mono ${
                i === 0 ? "bg-[color:var(--sunshine)] text-[color:var(--deep)]" :
                i === 1 ? "bg-slate-200 text-slate-700" :
                i === 2 ? "bg-orange-200 text-orange-800" :
                "bg-slate-100 text-slate-500"
              }`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{row.name}</div>
                <div className="text-[11px] text-slate-500">{row.won_bets}/{row.total_bets} paris gagnés</div>
              </div>
              <div className="font-mono font-bold tabular-nums text-[color:var(--deep)]">
                {row.wallet_points}<span className="text-xs opacity-60 ml-1">pts</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tint, testid }) {
  const tints = {
    coral: "bg-[color:var(--coral)]/10 text-[color:var(--coral)]",
    ocean: "bg-[color:var(--ocean)]/10 text-[color:var(--ocean)]",
    sunshine: "bg-[color:var(--sunshine)]/20 text-amber-700",
  };
  return (
    <div className="bento-tile bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${tints[tint]}`}>
        <Icon className="h-5 w-5" strokeWidth={2.4} />
      </div>
      <div className="mt-4 font-mono text-3xl font-extrabold tabular-nums text-[color:var(--deep)]">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
    </div>
  );
}

function EmptyStages() {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
      <Sailboat className="h-8 w-8 mx-auto text-slate-300 mb-3" />
      <div className="font-display font-bold text-lg text-[color:var(--deep)]">Aucune étape ouverte</div>
      <p className="text-sm text-slate-500 mt-1">L'admin doit créer les étapes du tour.</p>
    </div>
  );
}
